import { useMemo, type ComponentProps, type ReactNode } from "react";
import { Descriptions } from "@mijadesign/web";
import { useSlotRuntime, type SlotArea } from "../../slot-runtime";
import {
  buildDescriptionsSlotData,
  normalizeDescriptionsItemChildren,
} from "./normalize";

type DescriptionsItem = NonNullable<
  ComponentProps<typeof Descriptions>["items"]
>[number] & {
  key?: string;
};

interface DescriptionsWrapperProps extends Omit<
  ComponentProps<typeof Descriptions>,
  "items"
> {
  items?: DescriptionsItem[];
  renderSlot?: (
    slotName: string,
    payload?: Record<string, unknown>,
  ) => ReactNode;
  children?: ReactNode;
  runtime?: {
    context?: Record<string, unknown>;
  };
}

function getItemSlotName(item: DescriptionsItem, index: number) {
  const key = String(item.key || "").trim();
  return key ? `item:${key}` : `item:${index + 1}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function buildDescriptionsSlotRuntimeContext(
  runtimeContext: Record<string, unknown> | undefined,
  item: DescriptionsItem,
  index: number,
  slotName: string,
): Record<string, unknown> {
  const baseData = isPlainObject(runtimeContext?.data)
    ? (runtimeContext.data as Record<string, unknown>)
    : {};

  return {
    ...(runtimeContext || {}),
    scope: "descriptions.item",
    area: slotName,
    container: {
      type: "descriptions",
      area: slotName,
      index,
      item,
    },
    data: {
      ...baseData,
      ...buildDescriptionsSlotData(item as any, index),
    },
  };
}

function DescriptionsWrapper({
  items = [],
  renderSlot,
  children,
  runtime,
  ...restProps
}: DescriptionsWrapperProps) {
  const runtimeContext = runtime?.context;
  const slotAreas = useMemo<SlotArea[]>(
    () =>
      items.map((item, index) => ({
        name: getItemSlotName(item, index),
      })),
    [items],
  );
  const { getAreaNodes } = useSlotRuntime({
    renderSlot,
    children,
    areas: slotAreas,
    context: runtimeContext ? { runtimeContext } : undefined,
  });

  const finalItems = useMemo(
    () =>
      items.map((item, index) => {
        const normalizedItem = {
          ...item,
          children: normalizeDescriptionsItemChildren(
            item.children,
          ) as typeof item.children,
        };
        const slotName = getItemSlotName(item, index);
        const slotRuntimeContext = buildDescriptionsSlotRuntimeContext(
          runtimeContext,
          item,
          index,
          slotName,
        );
        const slotNodes = getAreaNodes(slotName, {
          item: normalizedItem,
          index,
          runtimeContext: slotRuntimeContext,
        });
        if (!slotNodes || slotNodes.length === 0) return normalizedItem;
        return {
          ...normalizedItem,
          children: slotNodes.length === 1 ? slotNodes[0] : slotNodes,
        };
      }),
    [items, getAreaNodes, runtimeContext],
  );

  return <Descriptions {...restProps} items={finalItems} />;
}

export default DescriptionsWrapper;
